import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const WochenplanContainer = styled.div`
  min-height: 100vh;
  padding-top: 100px;
`;

const HeroSection = styled.section`
  position: relative;
  height: 20vh;
  background: ${props => props.theme.colors.gradients.hero};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  overflow: hidden;
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${props => props.theme.fonts.script};
  font-size: clamp(3rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.4);
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.backgroundLight};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const TimelineContainer = styled.div`
  position: relative;
  margin-top: 3rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 50%;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.theme.colors.primary};
    border-radius: 2px;
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 2rem;
    }
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  margin-bottom: 3rem;
  position: relative;
  
  &:nth-child(even) {
    flex-direction: row-reverse;
    
    .timeline-content {
      margin-right: 3rem;
      margin-left: 0;
      
      @media (max-width: 768px) {
        margin-right: 0;
        margin-left: 3rem;
      }
    }
  }
  
  &:nth-child(odd) {
    .timeline-content {
      margin-left: 3rem;
      
      @media (max-width: 768px) {
        margin-left: 3rem;
      }
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: row !important;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  border: 4px solid white;
  box-shadow: ${props => props.theme.shadows.md};
  z-index: 2;
  
  @media (max-width: 768px) {
    left: 2rem;
    transform: none;
  }
`;

const TimelineContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  box-shadow: ${props => props.theme.shadows.card};
  flex: 1;
  max-width: 45%;
  
  @media (max-width: 768px) {
    max-width: none;
    flex: 1;
  }
  
  .time {
    color: ${props => props.theme.colors.primary};
    font-weight: 600;
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
    margin-bottom: 1rem;
  }
  
  .activities {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 0.25rem 0;
      color: ${props => props.theme.colors.textLight};
      
      &::before {
        content: '🐴';
        margin-right: 0.5rem;
      }
    }
  }
`;

const DayVariations = styled.section`
  padding: 4rem 0;
  background: white;
`;

const VariationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const VariationCard = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundLight};
  padding: 2rem;
  border-radius: 20px;
  text-align: center;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
  
  h3 {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
  }
  
  p {
    color: ${props => props.theme.colors.textLight};
    line-height: 1.6;
  }
`;

const Wochenplan = () => {
  const weeklySchedule = [
    {
      day: 'Montag',
      icon: '🌅',
      activities: [
        {
          time: '',
          title: 'Heilpädagogisches Reiten',
          description: 'Individuelle Einzelstunden für besondere Förderung und Entwicklung'
        },
        {
          time: '16:00 Uhr',
          title: 'MuKi-Club',
          description: 'Mutter-Kind Aktivitäten'
        },
        {
          time: '17:00 Uhr',
          title: 'Ponyclub',
          description: ''
        }
      ]
    },
    {
      day: 'Dienstag',
      icon: '🌤️',
      activities: [
        {
          time: '15:00 Uhr',
          title: 'Ponyclub',
          description: ''
        },
        {
          time: '16:00 Uhr',
          title: 'Ponyclub',
          description: ''
        },
        {
          time: '17:00 Uhr',
          title: 'MuKi-Club',
          description: 'Mutter-Kind Aktivitäten'
        }
      ]
    },
    {
      day: 'Mittwoch',
      icon: '☀️',
      activities: [
        {
          time: '15:00 Uhr',
          title: 'Ponyclub',
          description: ''
        },
        {
          time: '16:00 Uhr',
          title: 'Ponyclub',
          description: ''
        }
      ]
    },
    {
      day: 'Donnerstag',
      icon: '🌤️',
      activities: [
        {
          time: '15:00 Uhr',
          title: 'Ponyclub',
          description: ''
        },
        {
          time: '16:00 Uhr',
          title: 'Ponyclub',
          description: ''
        },
        {
          time: '17:00 Uhr',
          title: 'Heilpädagogische Gruppe',
          description: ''
        }
      ]
    }
  ];


  return (
    <WochenplanContainer>
      <Helmet>
        <title>Wochenplan - Der Ponystall | Unsere Kurse und Aktivitäten</title>
        <meta name="description" content="Entdecken Sie unseren Wochenplan mit allen Kursen und Aktivitäten im Ponystall - von Ponyclub bis heilpädagogischem Reiten." />
      </Helmet>

      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            Unser Wochenplan
          </HeroTitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <div className="text-center">
            <h2 className="section-title">Unsere Kurse und Aktivitäten</h2>
            <p className="section-subtitle">
              Hier finden Sie unseren kompletten Wochenplan mit allen Kursen und Aktivitäten. 
              Von heilpädagogischem Reiten bis zum Ponyclub - für jeden ist etwas dabei.
            </p>
          </div>

          <TimelineContainer>
            {weeklySchedule.map((day, index) => (
              <TimelineItem
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <TimelineDot>{day.icon}</TimelineDot>
                <TimelineContent className="timeline-content">
                  <div className="time">{day.day}</div>
                  <h3>{day.day}</h3>
                  <ul className="activities">
                    {day.activities.map((activity, idx) => (
                      <li key={idx}>
                        <strong>{activity.time}</strong> - {activity.title}
                        {activity.description && <span>: {activity.description}</span>}
                      </li>
                    ))}
                  </ul>
                </TimelineContent>
              </TimelineItem>
            ))}
          </TimelineContainer>
        </Container>
      </ContentSection>

    </WochenplanContainer>
  );
};

export default Wochenplan;